// The seam the support components (and the admin Support tab) talk through.
// `asAdmin` picks the side: the same ticket reads differently to its requester
// and to the support team. Changes arrive over the supportTicketChanged
// subscription, which triggers network-only refetches; polling is a fallback.

import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  TicketCategory,
  TicketStatus,
  useAddTicketCommentMutation,
  useAdminTicketsQuery,
  useCreateSupportTicketMutation,
  useDeleteSupportTicketMutation,
  useDeleteTicketCommentMutation,
  useMarkTicketReadMutation,
  useMeQuery,
  useMyTicketsQuery,
  useSetTicketStatusMutation,
  useSupportTicketChangedSubscription,
  useSupportTicketQuery,
  useSupportUnreadCountQuery,
  useTicketCommentsQuery,
} from "generated/graphql";
import { useS3 } from "hooks/useS3";

import { ATTACHMENT_FOLDER, TICKETS_POLL_MS } from "./constants";
import { SupportActionResult, SupportTicket, TicketComment } from "./types";

type Reexecute = (opts?: { requestPolicy?: "network-only" }) => void;

/** Runs `onChange` with the ticket id of every live ticket event. */
const useTicketEvents = (onChange: (ticketId: number) => void) => {
  // Paused while logged out: an unauthenticated socket is rejected (4403).
  const [{ data: me }] = useMeQuery();
  const [{ data: event }] = useSupportTicketChangedSubscription({
    pause: !me?.me,
  });

  useEffect(() => {
    if (event?.supportTicketChanged != null) {
      onChange(event.supportTicketChanged);
    }
    // onChange is a fresh closure each render; only a new event should fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);
};

const usePoll = (reexecute: Reexecute, enabled = true) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") {
        reexecute({ requestPolicy: "network-only" });
      }
    }, TICKETS_POLL_MS);
    return () => clearInterval(timer);
  }, [reexecute, enabled]);
};

const firstError = (
  res: { error?: { message: string } },
  errors: { message?: string | null }[] | null | undefined,
  fallback: string,
): string | null =>
  errors?.length
    ? (errors[0].message ?? fallback)
    : res.error
      ? res.error.message
      : null;

export const useTickets = (
  asAdmin: boolean,
): {
  tickets: SupportTicket[];
  loading: boolean;
  error?: string;
} => {
  const [mine, refetchMine] = useMyTicketsQuery({
    pause: asAdmin,
    requestPolicy: "cache-and-network",
  });
  const [all, refetchAll] = useAdminTicketsQuery({
    pause: !asAdmin,
    requestPolicy: "cache-and-network",
  });

  const result = asAdmin ? all : mine;
  const reexecute = asAdmin ? refetchAll : refetchMine;
  usePoll(reexecute);
  useTicketEvents(() => reexecute({ requestPolicy: "network-only" }));

  return {
    tickets: (asAdmin ? all.data?.adminTickets : mine.data?.myTickets) ?? [],
    // Initial load only, so refetches don't flash a spinner.
    loading: result.fetching && !result.data,
    error: result.error?.message,
  };
};

export const useTicket = (
  ticketId: number | null,
  asAdmin: boolean,
): {
  ticket: SupportTicket | null;
  comments: TicketComment[];
  loading: boolean;
  refresh: () => void;
} => {
  const variables = { id: ticketId ?? 0, asAdmin };
  const [ticketResult, refetchTicket] = useSupportTicketQuery({
    variables,
    pause: ticketId == null,
    requestPolicy: "cache-and-network",
  });
  const [commentsResult, refetchComments] = useTicketCommentsQuery({
    variables: { ticketId: ticketId ?? 0, asAdmin },
    pause: ticketId == null,
    requestPolicy: "cache-and-network",
  });

  const refresh = useCallback(() => {
    refetchTicket({ requestPolicy: "network-only" });
    refetchComments({ requestPolicy: "network-only" });
  }, [refetchTicket, refetchComments]);

  usePoll(refresh, ticketId != null);
  useTicketEvents((changedId) => {
    if (changedId === ticketId) {
      refresh();
    }
  });

  return {
    ticket: ticketResult.data?.supportTicket ?? null,
    comments: commentsResult.data?.ticketComments ?? [],
    loading:
      (ticketResult.fetching && !ticketResult.data) ||
      (commentsResult.fetching && !commentsResult.data),
    refresh,
  };
};

/** Tickets with unread replies, for the dashboard menu badge. */
export const useSupportUnreadCount = (asAdmin: boolean, enabled = true) => {
  const [{ data }, reexecute] = useSupportUnreadCountQuery({
    variables: { asAdmin },
    pause: !enabled,
    requestPolicy: "cache-and-network",
  });
  usePoll(reexecute, enabled);
  useTicketEvents(() => {
    if (enabled) {
      reexecute({ requestPolicy: "network-only" });
    }
  });

  return data?.supportUnreadCount ?? 0;
};

export const useTicketActions = (asAdmin: boolean) => {
  const { uploadToS3 } = useS3();
  const { t } = useTranslation("support");
  const [, createMut] = useCreateSupportTicketMutation();
  const [, commentMut] = useAddTicketCommentMutation();
  const [, statusMut] = useSetTicketStatusMutation();
  const [, markReadMut] = useMarkTicketReadMutation();
  const [, deleteCommentMut] = useDeleteTicketCommentMutation();
  const [, deleteTicketMut] = useDeleteSupportTicketMutation();

  const upload = useCallback(
    (files: File[]) =>
      Promise.all(
        files.map(async (file) => {
          const url = await uploadToS3(file, ATTACHMENT_FOLDER);
          if (!url) {
            throw new Error(t("snack.upload_failed", { file: file.name }));
          }
          return { name: file.name, url };
        }),
      ),
    [uploadToS3, t],
  );

  const create = useCallback(
    async (input: {
      subject: string;
      category: TicketCategory;
      body: string;
      files: File[];
      portalSlug?: string;
    }): Promise<SupportActionResult & { ticketId?: number }> => {
      let attachments;
      try {
        attachments = await upload(input.files);
      } catch (err) {
        return { ok: false, error: (err as Error).message };
      }

      const res = await createMut({
        options: {
          subject: input.subject,
          category: input.category,
          body: input.body,
          attachments,
          portalSlug: input.portalSlug,
        },
      });
      const payload = res.data?.createSupportTicket;
      const error = firstError(res, payload?.errors, t("snack.open_failed"));
      if (error || !payload?.ticket) {
        return { ok: false, error: error ?? t("snack.open_failed") };
      }
      return { ok: true, ticketId: payload.ticket.id };
    },
    [createMut, upload, t],
  );

  const comment = useCallback(
    async (
      ticketId: number,
      body: string,
      files: File[],
    ): Promise<SupportActionResult> => {
      let attachments;
      try {
        attachments = await upload(files);
      } catch (err) {
        return { ok: false, error: (err as Error).message };
      }

      const res = await commentMut({
        options: { ticketId, body, attachments, asAdmin },
      });
      const error = firstError(
        res,
        res.data?.addTicketComment.errors,
        t("snack.comment_failed"),
      );
      return error ? { ok: false, error } : { ok: true };
    },
    [commentMut, upload, asAdmin, t],
  );

  const setStatus = useCallback(
    async (
      ticketId: number,
      status: TicketStatus,
    ): Promise<SupportActionResult> => {
      const res = await statusMut({ ticketId, status, asAdmin });
      const error = firstError(
        res,
        res.data?.setTicketStatus.errors,
        t("snack.update_failed"),
      );
      return error ? { ok: false, error } : { ok: true };
    },
    [statusMut, asAdmin, t],
  );

  const markRead = useCallback(
    async (ticketId: number) => {
      // The returned view is normalized, so list badges clear without a refetch.
      await markReadMut({ ticketId, asAdmin });
    },
    [markReadMut, asAdmin],
  );

  const deleteComment = useCallback(
    async (commentId: number): Promise<SupportActionResult> => {
      const res = await deleteCommentMut({ commentId });
      return res.error || !res.data?.deleteTicketComment
        ? {
            ok: false,
            error: res.error?.message ?? t("snack.delete_comment_failed"),
          }
        : { ok: true };
    },
    [deleteCommentMut, t],
  );

  const deleteTicket = useCallback(
    async (ticketId: number): Promise<SupportActionResult> => {
      const res = await deleteTicketMut({ ticketId });
      return res.error || !res.data?.deleteSupportTicket
        ? {
            ok: false,
            error: res.error?.message ?? t("snack.delete_ticket_failed"),
          }
        : { ok: true };
    },
    [deleteTicketMut, t],
  );

  return { create, comment, setStatus, markRead, deleteComment, deleteTicket };
};
